import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ArrowRight, BookOpenText, ChevronLeft, Search, Sparkles } from 'lucide-react'
import { api } from '@/lib/api'
import { localizedName } from '@/lib/format'
import { useAppStore } from '@/lib/store'
import { useT } from '@/lib/i18n'
import { usePageTitle } from '@/lib/usePageTitle'
import { useStoreTheme } from '@/lib/useStoreTheme'
import type { StaticPage } from '@/shared/types'

const LEGAL_SLUGS = new Set([
  'about',
  'delivery',
  'privacy-policy',
  'privacy',
  'terms',
  'terms-of-service',
  'rules',
  'dostavka-ta-oplata',
  'dostavka',
  'delivery-and-payment',
  'garantiya-ta-povernennya',
  'garantiya',
  'warranty',
  'warranty-and-returns',
  'oplata',
  'payment',
  'returns',
  'public-offer',
  'oferta',
])

export function StoriesPage() {
  const lang = useAppStore((s) => s.lang)
  const T = useT('home')
  const tStories = useT('stories')
  const tNav = useT('nav')
  const [searchQuery, setSearchQuery] = useState('')

  const { data: settings } = useQuery<{ shop_name?: string }>({
    queryKey: ['public-settings'],
    queryFn: () => api.get('/settings'),
    staleTime: 300_000,
  })

  const { data: pages, isLoading } = useQuery<StaticPage[]>({
    queryKey: ['pages'],
    queryFn: () => api.get<StaticPage[]>('/pages'),
    staleTime: 300_000,
  })

  const theme = useStoreTheme()
  const isMayParfum =
    theme.isMayParfum ||
    (settings?.shop_name ?? '')
      .toLowerCase()
      .replace(/\s+/g, '')
      .includes('mayparfum')

  // Filter out legal/service pages to only show educational articles & guides
  const storyPages = useMemo(() => {
    return (pages ?? []).filter((page) => {
      if (LEGAL_SLUGS.has(page.slug)) return false
      return true
    })
  }, [pages])

  const filteredPages = useMemo(() => {
    if (!searchQuery.trim()) return storyPages
    const q = searchQuery.toLowerCase().trim()
    return storyPages.filter((page) => {
      const title = localizedName(lang, page.title_uk, page.title_en).toLowerCase()
      const content = localizedName(lang, page.content_uk, page.content_en).toLowerCase()
      return title.includes(q) || content.includes(q)
    })
  }, [storyPages, searchQuery, lang])

  usePageTitle(
    lang === 'en'
      ? 'Guides & Articles'
      : isMayParfum
      ? 'Парфумерний гід та статті'
      : lang === 'ru'
      ? 'Статьи и инструкции'
      : 'Статті та інструкції'
  )

  if (isMayParfum) {
    return (
      <div className="min-h-screen bg-[#3a4352] px-4 pb-28 pt-6 text-[#f8f3ea] md:px-8">
        <div className="mx-auto max-w-4xl">
          {/* Header Navigation */}
          <div className="mb-6 flex items-center justify-between">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#b9aa99] transition hover:text-white"
            >
              <ChevronLeft className="h-4 w-4" />
              {tNav.home}
            </Link>
            <Link
              to="/quiz"
              className="inline-flex items-center gap-1.5 rounded-xl border border-[#bc9050]/40 bg-[#2d333e] px-3 py-1.5 text-xs font-bold text-[#d7b171] transition hover:border-[#bc9050]"
            >
              <Sparkles className="h-3.5 w-3.5" />
              {tStories.fragranceQuiz}
            </Link>
          </div>

          <header className="mb-8">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#d7b171]">
              {T.mayparfumStoriesTitle || tStories.storiesCultureTitle}
            </p>
            <h1 className="mt-2 text-3xl font-bold text-white md:text-4xl">
              {T.mayparfumStoriesSubtitle || tStories.storiesCultureSubtitle}
            </h1>
            <p className="mt-2 text-sm text-[#b9aa99]">
              {tStories.storiesCultureDescription}
            </p>
          </header>

          {/* Search Input */}
          <div className="relative mb-8">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#b9aa99]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={tStories.searchPlaceholder}
              className="w-full rounded-2xl border border-[#6f5840]/30 bg-[#2d333e] py-3.5 pl-11 pr-4 text-sm text-[#f8f3ea] placeholder-[#8a95a5] shadow-inner transition focus:border-[#bc9050] focus:outline-none focus:ring-1 focus:ring-[#bc9050]"
            />
          </div>

          {isLoading ? (
            <div className="flex justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#bc9050] border-t-transparent" />
            </div>
          ) : filteredPages.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-[#6f5840]/30 bg-[#2d333e] p-12 text-center text-[#b9aa99]">
              <BookOpenText className="mb-3 h-12 w-12 text-[#bc9050]/50" />
              <p className="text-base font-medium">
                {tStories.noArticlesFound}
              </p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {filteredPages.map((page) => {
                const title = localizedName(lang, page.title_uk, page.title_en)
                const content = localizedName(lang, page.content_uk, page.content_en) || ''
                const cleanSnippet = content
                  .replace(/[#*_\n\r`]+/g, ' ')
                  .replace(/\s+/g, ' ')
                  .trim()

                return (
                  <Link
                    key={page.slug}
                    to="/pages/$slug"
                    params={{ slug: page.slug }}
                    className="group flex flex-col justify-between rounded-2xl border border-[#6f5840]/30 bg-gradient-to-br from-[#2d333e] to-[#252b35] p-5 shadow-md transition-all duration-200 hover:border-[#bc9050]/70 hover:shadow-xl hover:shadow-[0_4px_20px_rgba(188,144,80,0.15)]"
                  >
                    <div>
                      <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-xl bg-[#bc9050]/15 text-[#d7b171] ring-1 ring-[#bc9050]/30 transition group-hover:scale-105">
                        <BookOpenText className="h-4 w-4" />
                      </div>
                      <h2 className="text-base font-bold leading-snug text-[#f8f3ea] transition group-hover:text-[#d7b171]">
                        {title}
                      </h2>
                      <p className="mt-2.5 line-clamp-3 text-xs leading-relaxed text-[#b9aa99]">
                        {cleanSnippet}
                      </p>
                    </div>
                    <div className="mt-5 flex items-center gap-1.5 pt-3 border-t border-[#6f5840]/20 text-xs font-bold uppercase tracking-wider text-[#d7b171]">
                      <span>{tStories.readGuide}</span>
                      <ArrowRight className="h-3 w-3 transition group-hover:translate-x-1" />
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      </div>
    )
  }

  // Generic / store-themed version for other stores
  return (
    <div
      className={`min-h-screen px-4 pb-28 pt-6 md:px-8 ${
        theme.isDarkTheme ? 'bg-[#15181c] text-slate-100' : 'bg-gray-50 text-gray-900'
      }`}
    >
      <div className="mx-auto max-w-4xl">
        <div className="mb-6 flex items-center justify-between">
          <Link
            to="/"
            className={`inline-flex items-center gap-1 text-sm font-semibold transition ${
              theme.isDarkTheme ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
            }`}
          >
            <ChevronLeft className="h-4 w-4" />
            {tNav.home}
          </Link>
        </div>

        <header className="mb-8">
          <h1
            className={`text-3xl font-bold ${
              theme.isDarkTheme ? 'text-white' : 'text-gray-900'
            }`}
          >
            {lang === 'en'
              ? 'Articles & Guides'
              : lang === 'ru'
              ? 'Статьи и инструкции'
              : 'Статті та інструкції'}
          </h1>
        </header>

        {/* Search */}
        <div className="relative mb-6">
          <Search
            className={`absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 ${
              theme.isDarkTheme ? 'text-slate-400' : 'text-gray-400'
            }`}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={
              lang === 'en'
                ? 'Search articles...'
                : lang === 'ru'
                ? 'Поиск среди статей...'
                : 'Пошук серед статей...'
            }
            className={`w-full rounded-2xl border py-3 pl-10 pr-4 text-sm shadow-sm focus:outline-none focus:ring-1 ${
              theme.isDarkTheme
                ? 'border-[#2d3748] bg-[#1f242d] text-white placeholder-slate-500 focus:border-amber-400 focus:ring-amber-400'
                : 'border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:border-brand-500 focus:ring-brand-500'
            }`}
          />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <div
              className={`h-8 w-8 animate-spin rounded-full border-2 border-t-transparent ${
                theme.isDarkTheme ? 'border-amber-400' : 'border-brand-600'
              }`}
            />
          </div>
        ) : filteredPages.length === 0 ? (
          <div
            className={`flex flex-col items-center justify-center rounded-2xl border p-12 text-center ${
              theme.isDarkTheme
                ? 'border-[#2d3748] bg-[#1f242d] text-slate-400'
                : 'border-gray-200 bg-white text-gray-400'
            }`}
          >
            <BookOpenText
              className={`mb-3 h-12 w-12 opacity-40 ${
                theme.isDarkTheme ? 'text-amber-400' : 'text-brand-600'
              }`}
            />
            <p>
              {lang === 'en'
                ? 'No articles found.'
                : lang === 'ru'
                ? 'Статей не найдено.'
                : 'Статей не знайдено.'}
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {filteredPages.map((page) => (
              <Link
                key={page.slug}
                to="/pages/$slug"
                params={{ slug: page.slug }}
                className={`group flex flex-col justify-between rounded-2xl border p-5 shadow-sm transition ${
                  theme.isDarkTheme
                    ? 'border-[#2d3748] bg-[#1f242d] hover:border-amber-400/50 hover:shadow-md'
                    : 'border-gray-200 bg-white hover:border-brand-500 hover:shadow-md'
                }`}
              >
                <div>
                  <h2
                    className={`text-base font-bold transition ${
                      theme.isDarkTheme
                        ? 'text-slate-100 group-hover:text-amber-400'
                        : 'text-gray-900 group-hover:text-brand-600'
                    }`}
                  >
                    {localizedName(lang, page.title_uk, page.title_en)}
                  </h2>
                  <p
                    className={`mt-2 line-clamp-3 text-xs leading-relaxed ${
                      theme.isDarkTheme ? 'text-slate-400' : 'text-gray-500'
                    }`}
                  >
                    {localizedName(lang, page.content_uk, page.content_en)}
                  </p>
                </div>
                <span
                  className={`mt-4 inline-flex items-center gap-1 text-xs font-semibold ${
                    theme.isDarkTheme ? 'text-amber-400' : 'text-brand-600'
                  }`}
                >
                  {lang === 'en' ? 'Read more' : lang === 'ru' ? 'Читать далее' : 'Читати далі'}
                  <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
